/**
 * The "Payment configuration" link on a management-invoice row.
 *
 * Renders a router <Link> when a router is mounted (every admin surface) and a
 * plain <a> otherwise, so the hook that shares row actions between the
 * Payments hub and the owner profile can render it without forcing a router
 * on each caller — or on each caller's tests. Renders nothing when the row
 * names no property (a legacy portfolio invoice has no single unit to configure).
 *
 * `forwardRef` + prop spreading is required here, not cosmetic: both kebab
 * menus (useManagementInvoiceActions.tsx) render this via
 * `<DropdownMenuItem asChild><PaymentConfigurationAnchor .../></DropdownMenuItem>`.
 * Radix's `asChild` clones its child and injects `ref`/`role`/`tabIndex`/the
 * keyboard & pointer handlers that drive roving focus and Enter-to-activate —
 * a plain function component that ignores those props silently drops them,
 * so the item renders but can't be reached or activated by keyboard.
 */
import { forwardRef, type AnchorHTMLAttributes, type ReactNode } from "react";
import { Link, useInRouterContext } from "react-router-dom";
import {
  paymentConfigurationPath,
  type PaymentConfigurationTarget,
} from "@/lib/payment-configuration-link";

export const PaymentConfigurationAnchor = forwardRef<
  HTMLAnchorElement,
  AnchorHTMLAttributes<HTMLAnchorElement> & {
    row: PaymentConfigurationTarget;
    title?: string;
    children: ReactNode;
  }
>(function PaymentConfigurationAnchor(
  { row, className, title, children, ...anchorProps },
  ref
) {
  const inRouter = useInRouterContext();
  const to = paymentConfigurationPath(row);
  if (!to) return null;
  const label = title ?? "Payment configuration";
  return inRouter ? (
    <Link
      ref={ref}
      to={to}
      className={className}
      title={label}
      data-testid="payment-configuration-link"
      {...anchorProps}
    >
      {children}
    </Link>
  ) : (
    <a
      ref={ref}
      href={to}
      className={className}
      title={label}
      data-testid="payment-configuration-link"
      {...anchorProps}
    >
      {children}
    </a>
  );
});
